import { TableOfContents } from "@/components/table-of-contents";
import { seo } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/legal/_layout/privacy")({
  component: PrivacyPolicy,
  head: () => {
    const title = "Privacy Policy | Inkriv";
    const description =
      "Learn how Inkriv collects, uses, and protects your personal data.";
    const image = "https://inkriv.com/legal.jpg";

    return {
      title,
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        ...seo({ title, description, image }),
      ],
    };
  },
});

function PrivacyPolicy() {
  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "information-we-collect", title: "Information We Collect" },
    { id: "ai-content", title: "AI-generated content" },
    { id: "how-we-use-information", title: "How We Use Your Information" },
    { id: "data-storage", title: "Data Storage and Security" },
    { id: "your-rights", title: "Your Rights" },
    { id: "cookies", title: "Cookies and Tracking" },
    { id: "third-parties", title: "Third-Party Services" },
    { id: "changes", title: "Changes to This Policy" },
    { id: "contact", title: "Contact Us" },
  ];

  return (
    <div className="flex flex-col md:flex-row gap-8">
      <div className="md:w-1/4 order-2 md:order-1">
        <div className="sticky top-8">
          <TableOfContents sections={sections} />
        </div>
      </div>

      <div className="md:w-3/4 order-1 md:order-2">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h1 className="text-lg font-bold text-neutral-900 mb-6 font-eligoue">
            Privacy Policy
          </h1>
          <p className=" text-neutral-700 mb-8 font-satoshi">
            Last Updated: May 2nd, 2025
          </p>

          <section id="introduction" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              Introduction
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              Hey there! We're committed to protecting your privacy while
              providing you with an awesome AI writing experience. This policy
              explains how we collect, use, and protect your information when
              you use our personalized AI writing tool.
            </p>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              Our tool is designed to help you write like you—which means we
              need to understand your writing style. We take that responsibility
              seriously and want to be fully transparent about how we handle
              your data.
            </p>
          </section>

          <section id="information-we-collect" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              Information We Collect
            </h2>
            <div className="space-y-4">
              <div className="bg-neutral-100 p-4 rounded-md">
                <h3 className=" font-semibold text-primary-700 mb-2 font-eligoue">
                  Information you provide us:
                </h3>
                <ul className="list-disc pl-5  text-neutral-800 font-satoshi text-xs">
                  <li>Account information (name, email, password)</li>
                  <li>Writing samples you upload or create</li>
                  <li>Feedback and preferences you share</li>
                  <li>
                    Payment info (handled securely via payment processors)
                  </li>
                </ul>
              </div>

              <div className="bg-neutral-100 p-4 rounded-md">
                <h3 className=" font-semibold text-primary-700 mb-2 font-eligoue">
                  Information we collect automatically:
                </h3>
                <ul className="list-disc pl-5  text-neutral-800 font-satoshi text-xs">
                  <li>Usage data (how you interact with our tool)</li>
                  <li>Device information (browser type, operating system)</li>
                  <li>IP address and location data</li>
                  <li>Writing patterns and style characteristics</li>
                </ul>
              </div>
            </div>
          </section>

          <section id="ai-content" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              AI-generated content
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              We process your inputs and outputs through third-party AI models.
              We may temporarily store this data to personalize your experience
              and resolve technical issues.
            </p>
          </section>

          <section id="how-we-use-information" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              How We Use Your Information
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              We use your data to:
            </p>
            <ul className="list-disc pl-5  text-neutral-800 mb-4 font-satoshi text-xs">
              <li>Personalize the AI to match your writing style</li>
              <li>Improve and optimize the tool</li>
              <li>Communicate with you about your account and updates</li>
              <li>Process payments and manage subscriptions</li>
              <li>Keep the platform secure</li>
              <li>Comply with legal obligations</li>
            </ul>
            <div className="bg-secondary-50 p-4 rounded-md border-l-4 border-secondary-100">
              <p className=" text-neutral-800 font-satoshi">
                <span className="font-semibold">Important:</span> We never sell
                your personal data or writing samples. Your style is yours—we're
                just here to help you express it better.
              </p>
            </div>
          </section>

          <section id="data-storage" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              Data Storage and Security
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              We take data security seriously. Here's how we protect your
              information:
            </p>
            <ul className="list-disc pl-5  text-neutral-800 mb-4 font-satoshi text-xs">
              <li>Encryption (in transit and at rest)</li>
              <li>Regular security audits and updates</li>
              <li>Access restrictions to personal info</li>
              <li>Industry-standard cloud infrastructure</li>
            </ul>
            <p className=" text-neutral-800 font-satoshi">
              We retain your data for as long as your account is active or as
              needed to provide you with our services. If you delete your
              account, we'll remove or anonymize your personal information
              within 30 days, except where we need to retain certain information
              for legitimate business or legal purposes.
            </p>
          </section>

          <section id="your-rights" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              Your Rights
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              Depending on your location, you may have the right to:
            </p>

            <div className="bg-neutral-100 p-4 rounded-md">
              <ul className="list-disc pl-5  text-neutral-800 font-satoshi text-xs">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your personal data</li>
                <li>Restrict or object to processing</li>
                <li>Export your data (data portability)</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </div>

            <p className=" text-neutral-800 mt-4 font-satoshi">
              To exercise these rights, please contact us using the information
              in the "Contact Us" section.
            </p>
          </section>

          <section id="cookies" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              Cookies and Tracking
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-5  text-neutral-800 mb-4 font-satoshi text-xs">
              <li>Enhance your experience</li>
              <li>Understand how you use the tool</li>
              <li>Support marketing efforts</li>
            </ul>
            <div className="bg-neutral-100 p-4 rounded-md">
              <h3 className=" font-semibold text-primary-700 mb-2 font-eligoue">
                Types of cookies we use:
              </h3>
              <ul className="list-disc pl-5 text-neutral-800 font-satoshi text-xs">
                <li>
                  <span className="font-semibold">Essential cookies:</span>{" "}
                  Required for the service to function
                </li>
                <li>
                  <span className="font-semibold">Preference cookies:</span>{" "}
                  Remember your settings and preferences
                </li>
                <li>
                  <span className="font-semibold">Analytics cookies:</span> Help
                  us understand how you use our service
                </li>
                <li>
                  <span className="font-semibold">Marketing cookies:</span> Used
                  to deliver relevant ads and track campaign performance
                </li>
              </ul>
            </div>
            <p className=" text-neutral-800 mb-4 font-satoshi mt-3">
              You can manage cookies via your browser settings.
            </p>
          </section>

          <section id="third-parties" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              Third-Party Services
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              We work with trusted partners to run Inkriv. These services may
              access your information only as needed to support the platform and
              are contractually obligated to protect your data.
            </p>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              Examples of third-party services we use:
            </p>
            <ul className="list-disc pl-5  text-neutral-800 mb-4 font-satoshi text-xs">
              <li>Cloud hosting and storage providers</li>
              <li>Payment processors</li>
              <li>Analytics services</li>
              <li>Customer support tools</li>
              <li>Communication platforms</li>
              <li>AI model providers</li>
            </ul>
          </section>

          <section id="changes" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              Changes to This Policy
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              We may update this Privacy Policy from time to time. If we make
              material changes, we'll let you know via email or on the site
              before they take effect.
            </p>
            <p className=" text-neutral-800 font-satoshi">
              The "Last Updated" date reflects the most recent changes.
            </p>
          </section>

          <section id="contact" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-eligoue">
              Contact Us
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              Have questions, concerns, or requests?
            </p>
            <div className="bg-neutral-50 p-6 rounded-md text-center">
              <p className=" font-semibold text-primary-700 mb-2 font-eligoue">
                Email: support@inkriv.com
              </p>
              <p className=" text-primary-600 font-satoshi">
                We're committed to addressing your concerns and will respond
                promptly.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
